/**
 * Global CSS for the Pediatrack app.
 *
 * Writes the design tokens from `buildCssVars()` onto the root element
 * as CSS custom properties so legacy `.css` files can read the same
 * values.
 */
import { GlobalStyles as MuiGlobalStyles } from "@mui/material";
import { buildCssVars } from "./tokens";

export function AppGlobalStyles() {
    return (
        <MuiGlobalStyles
            styles={{
                ":root": buildCssVars(),
                "html, body, #root": {
                    height: "100%",
                    width: "100%",
                },
                body: {
                    margin: 0,
                    background: "var(--pt-bg)",
                    color: "var(--pt-text-primary)",
                    fontFamily:
                        "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                },
                "*, *::before, *::after": { boxSizing: "border-box" },
                ":focus-visible": {
                    outline: "2px solid var(--pt-primary)",
                    outlineOffset: 2,
                    borderRadius: 4,
                },
                "input, select, textarea": {
                    fontFamily: "inherit",
                    color: "var(--pt-text-primary)",
                },
                "input::placeholder, textarea::placeholder": {
                    color: "var(--pt-text-disabled)",
                },
                "button": {
                    fontFamily: "inherit",
                },
                /* Common utility */
                ".required-star": { color: "var(--pt-error)" },
                /* Skeleton keyframes — MUI default "wave" relies on these */
                "@keyframes mui-wave": {
                    "0%": { transform: "translateX(-100%)" },
                    "100%": { transform: "translateX(100%)" },
                },
                /* Bento / home page motion primitives */
                "@keyframes wp-fade-up": {
                    "0%": { opacity: 0, transform: "translateY(12px)" },
                    "100%": { opacity: 1, transform: "translateY(0)" },
                },
                "@keyframes wp-float": {
                    "0%, 100%": { transform: "translateY(0) rotate(0deg)" },
                    "50%": { transform: "translateY(-8px) rotate(2deg)" },
                },
                "@keyframes wp-pulse-glow": {
                    "0%, 100%": {
                        boxShadow:
                            "0 0 0 0 rgba(239,68,68,0.55), 0 0 0 0 rgba(239,68,68,0.30)",
                        opacity: 1,
                    },
                    "50%": {
                        boxShadow:
                            "0 0 0 8px rgba(239,68,68,0.0), 0 0 0 16px rgba(239,68,68,0.0)",
                        opacity: 0.85,
                    },
                },
                "@keyframes wp-shimmer": {
                    "0%": { backgroundPosition: "-200% 0" },
                    "100%": { backgroundPosition: "200% 0" },
                },
                "@keyframes wp-gradient-pan": {
                    "0%, 100%": { backgroundPosition: "0% 50%" },
                    "50%": { backgroundPosition: "100% 50%" },
                },
                /* Stagger utility — children fade up with cascading delay. */
                ".wp-stagger > *": {
                    animation: "wp-fade-up 520ms var(--pt-ease-out-expo, cubic-bezier(0.16,1,0.3,1)) both",
                },
                ".wp-stagger > *:nth-child(1)": { animationDelay: "0ms" },
                ".wp-stagger > *:nth-child(2)": { animationDelay: "60ms" },
                ".wp-stagger > *:nth-child(3)": { animationDelay: "120ms" },
                ".wp-stagger > *:nth-child(4)": { animationDelay: "180ms" },
                ".wp-stagger > *:nth-child(5)": { animationDelay: "240ms" },
                ".wp-stagger > *:nth-child(6)": { animationDelay: "300ms" },
                "@media (prefers-reduced-motion: reduce)": {
                    ".wp-stagger > *": { animation: "none" },
                },
            }}
        />
    );
}
